import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

export default function MultiSeriesLineChart({
  data = [],
  xAccessor = d => d.x,
  yAccessor = d => d.y,
  seriesAccessor = d => d.series,
  lineStyleAccessor = null, // Function to determine if line should be dashed based on data
  lineWidthAccessor = null, // Function to determine line width based on data
  xLabel = '',
  yLabel = '',
  plotMargin = { top: 30, right: 200, bottom: 50, left: 70 },
  transitionDuration = 1000,
  showPoints = true,
  showLines = true,
  pointRadius = 2.5,
  lineWidth = 2,
  colors = d3.schemeTableau10,
  xDomain: xDomainOverride,
  yDomain: yDomainOverride,
  brushEnabled = true,
  tooltipRenderer
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const clipIdRef = useRef(`clip-${Math.random().toString(36).slice(2, 10)}`);
  const [plotSize, setPlotSize] = useState(480);
  const [brushDomain, setBrushDomain] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, d: null });
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    function handleResize() {
      if (containerRef.current) {
        const size = Math.max(360, Math.min(containerRef.current.offsetWidth, 800));
        setPlotSize(size);
      }
      if (typeof window !== 'undefined') {
        setWindowWidth(window.innerWidth);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setTimeout(handleResize, 0);
      });
    }, { threshold: 0.1 });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => {
      window.removeEventListener('resize', handleResize);
      obs.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return;

    const svg = d3.select(svgRef.current);

    // Determine responsive layout
    const isLegendBottom = windowWidth < 950;
    const shouldTiltXAxis = windowWidth < 550;
    
    // Adjust dimensions based on responsive layout
    const extraWidthForLegend = isLegendBottom ? 0 : 100;
    const extraHeightForLegend = isLegendBottom ? 60 : 0;
    const extraHeightForTiltedLabels = shouldTiltXAxis ? 20 : 0;
    
    const width = plotSize + extraWidthForLegend;
    const height = Math.round(plotSize * 0.66) + extraHeightForLegend + extraHeightForTiltedLabels;
    const margin = {
      ...plotMargin,
      bottom: plotMargin.bottom + extraHeightForLegend + extraHeightForTiltedLabels
    };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    svg.attr('width', width).attr('height', height);

    // Ensure defs and clipPath
    let defs = svg.select('defs');
    if (defs.empty()) {
      defs = svg.insert('defs', ':first-child');
    }
    let clip = defs.select(`#${clipIdRef.current}`);
    if (clip.empty()) {
      clip = defs.append('clipPath').attr('id', clipIdRef.current);
      clip.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', innerWidth)
        .attr('height', innerHeight);
    } else {
      clip.select('rect')
        .attr('width', innerWidth)
        .attr('height', innerHeight);
    }

    // Root group
    let root = svg.select('g.main-group');
    if (root.empty()) {
      root = svg.append('g').attr('class', 'main-group');
    }
    root.attr('transform', `translate(${margin.left},${margin.top})`);

    // Prepare data
    const prepared = data
      .map(d => ({
        d,
        x: Number(xAccessor(d)),
        y: Number(yAccessor(d)),
        s: seriesAccessor ? seriesAccessor(d) : 'Series'
      }))
      .filter(p => isFinite(p.x) && isFinite(p.y));

    const groups = d3.groups(prepared, p => p.s)
      .map(([key, values]) => ({ 
        key, 
        values: values.sort((a, b) => a.x - b.x),
        isDashed: lineStyleAccessor ? lineStyleAccessor(values[0]?.d) : false,
        lineWidth: lineWidthAccessor ? lineWidthAccessor(values[0]?.d) : lineWidth
      }));

    const xExtent = d3.extent(prepared, p => p.x);
    const yExtent = d3.extent(prepared, p => p.y);

    let xDomain = xDomainOverride || [Math.min(0, xExtent[0] ?? 0), Math.max(1, xExtent[1] ?? 1)];
    if (brushDomain) xDomain = [brushDomain[0], brushDomain[1]];

    const x = d3.scaleLinear().domain(xDomain).range([0, innerWidth]).nice();
    
    // Calculate y-domain with padding if no override provided
    let yDomain;
    if (yDomainOverride) {
      yDomain = yDomainOverride;
    } else {
      const minY = Math.min(0, yExtent[0] ?? 0);
      const maxY = Math.max(1, yExtent[1] ?? 1);
      // Add 1% padding to the max value for better visual presentation
      yDomain = [minY, maxY * 1.01];
    }
    
    const y = d3.scaleLinear().domain(yDomain).range([innerHeight, 0]).nice();

    // Axes
    let xAxisG = root.select('g.x-axis');
    if (xAxisG.empty()) xAxisG = root.append('g').attr('class', 'x-axis').attr('transform', `translate(0,${innerHeight})`);
    else xAxisG.attr('transform', `translate(0,${innerHeight})`);
    
    const xAxis = d3.axisBottom(x).ticks(8);
    xAxisG.transition().duration(transitionDuration).call(xAxis);
    
    // Apply tilted labels if needed
    if (shouldTiltXAxis) {
      xAxisG.selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-45)");
    } else {
      xAxisG.selectAll("text")
        .style("text-anchor", "middle")
        .attr("dx", "0")
        .attr("dy", "0.71em")
        .attr("transform", "rotate(0)");
    }

    let yAxisG = root.select('g.y-axis');
    if (yAxisG.empty()) yAxisG = root.append('g').attr('class', 'y-axis');
    yAxisG.transition().duration(transitionDuration).call(d3.axisLeft(y).ticks(6));

    // Labels
    let xLbl = root.select('text.x-label');
    if (xLbl.empty()) xLbl = root.append('text').attr('class', 'x-label');
    xLbl
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 38)
      .attr('text-anchor', 'middle')
      .attr('font-size', 14)
      .text(xLabel);

    let yLbl = root.select('text.y-label');
    if (yLbl.empty()) yLbl = root.append('text').attr('class', 'y-label');
    yLbl
      .attr('transform', `rotate(-90)`) 
      .attr('x', -innerHeight / 2)
      .attr('y', -48)
      .attr('text-anchor', 'middle')
      .attr('font-size', 14)
      .text(yLabel);

    const color = d3.scaleOrdinal(colors).domain(groups.map(g => g.key));

    // Plot group with clipping
    let plotG = root.select('g.plot-group');
    if (plotG.empty()) plotG = root.append('g').attr('class', 'plot-group');
    plotG.attr('clip-path', `url(#${clipIdRef.current})`);

    // Line generator
    const line = d3.line()
      .x(p => x(p.x))
      .y(p => y(p.y))
      .defined(p => isFinite(p.x) && isFinite(p.y));

    // Lines (one per series) - only render if showLines is true
    if (showLines) {
      const lineSel = plotG.selectAll('path.line').data(groups, d => d.key);
      
      // Enter new lines
      lineSel.enter()
        .append('path')
        .attr('class', 'line')
        .attr('fill', 'none')
        .attr('stroke-width', d => d.lineWidth)
        .attr('stroke', d => color(d.key))
        .attr('stroke-dasharray', d => d.isDashed ? '5,5' : 'none')
        .attr('d', d => line(d.values))
        .attr('opacity', 0)
        .transition().duration(transitionDuration)
        .attr('opacity', 1);
      
      // Update existing lines
      lineSel
        .attr('stroke', d => color(d.key))
        .attr('stroke-width', d => d.lineWidth)
        .attr('stroke-dasharray', d => d.isDashed ? '5,5' : 'none')
        .transition().duration(transitionDuration)
        .attr('d', d => line(d.values))
        .attr('opacity', 1);
      
      // Remove old lines
      lineSel.exit().transition().duration(300).attr('opacity', 0).remove();
    } else {
      // Remove all lines if showLines is false
      plotG.selectAll('path.line').remove();
    }



    // Points (flattened)
    if (showPoints) {
      const flatPts = groups.flatMap(g => g.values.map((p, i) => ({ 
        key: `${g.key}-${i}-${p.x}-${p.y}`, 
        s: g.key, 
        x: p.x, 
        y: p.y, 
        originalData: p.d 
      })));
      const ptsSel = plotG.selectAll('circle.point').data(flatPts, d => d.key);
      ptsSel.enter()
        .append('circle')
        .attr('class', 'point')
        .attr('r', pointRadius)
        .attr('fill', d => color(d.s))
        .attr('cx', d => x(d.x))
        .attr('cy', d => y(d.y))
        .attr('opacity', 0)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          if (tooltipRenderer) {
            const rect = containerRef.current.getBoundingClientRect();
            setTooltip({
              visible: true,
              x: event.clientX - rect.left + 10,
              y: event.clientY - rect.top + 10,
              d: d.originalData
            });
          }
        })
        .on('mousemove', function(event) {
          if (tooltipRenderer) {
            const rect = containerRef.current.getBoundingClientRect();
            setTooltip(t => ({ ...t, x: event.clientX - rect.left + 10, y: event.clientY - rect.top + 10 }));
          }
        })
        .on('mouseout', function() {
          if (tooltipRenderer) {
            setTooltip({ visible: false, x: 0, y: 0, d: null });
          }
        })
        .transition().duration(transitionDuration)
        .attr('opacity', 0.9);
      ptsSel
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          if (tooltipRenderer) {
            const rect = containerRef.current.getBoundingClientRect();
            setTooltip({
              visible: true,
              x: event.clientX - rect.left + 10,
              y: event.clientY - rect.top + 10,
              d: d.originalData
            });
          }
        })
        .on('mousemove', function(event) {
          if (tooltipRenderer) {
            const rect = containerRef.current.getBoundingClientRect();
            setTooltip(t => ({ ...t, x: event.clientX - rect.left + 10, y: event.clientY - rect.top + 10 }));
          }
        })
        .on('mouseout', function() {
          if (tooltipRenderer) {
            setTooltip({ visible: false, x: 0, y: 0, d: null });
          }
        })
        .transition().duration(transitionDuration)
        .attr('fill', d => color(d.s))
        .attr('cx', d => x(d.x))
        .attr('cy', d => y(d.y))
        .attr('opacity', 0.9);
      ptsSel.exit().transition().duration(300).attr('opacity', 0).remove();
    } else {
      plotG.selectAll('circle.point').remove();
    }

    // Legend positioned responsively
    let legend = root.select('g.legend');
    if (legend.empty()) legend = root.append('g').attr('class', 'legend');
    const legendItems = legend.selectAll('g.item').data(groups, d => d.key);
    const legendEnter = legendItems.enter().append('g').attr('class', 'item');
    legendEnter.append('line').attr('x1', 0).attr('x2', 24).attr('y1', -8).attr('y2', -8).attr('stroke-width', 2);
    legendEnter.append('text').attr('x', 32).attr('y', -6).attr('alignment-baseline', 'hanging').attr('font-size', 12);
    
    if (isLegendBottom) {
      // Bottom legend layout - arrange items horizontally
      const itemWidth = Math.max(120, innerWidth / groups.length);
      legendItems.merge(legendEnter)
        .attr('transform', (d, i) => `translate(${i * itemWidth}, 0)`)
        .select('line')
        .attr('stroke', d => color(d.key))
        .attr('stroke-dasharray', d => d.isDashed ? '5,5' : 'none')
        .attr('opacity', showLines ? 1 : 0.3);
      legendItems.merge(legendEnter)
        .select('text').text(d => d.key + (d.isDashed ? ' (comparison)' : ''));
      // Position legend at bottom center
      legend.attr('transform', `translate(${Math.max(0, (innerWidth - groups.length * itemWidth) / 2)}, ${innerHeight + 40})`);
    } else {
      // Right side legend layout - arrange items vertically
      legendItems.merge(legendEnter)
        .attr('transform', (d, i) => `translate(0, ${i * 18})`)
        .select('line')
        .attr('stroke', d => color(d.key))
        .attr('stroke-dasharray', d => d.isDashed ? '5,5' : 'none')
        .attr('opacity', showLines ? 1 : 0.3);
      legendItems.merge(legendEnter)
        .select('text').text(d => d.key + (d.isDashed ? ' (comparison)' : ''));
      // Position legend in the right margin area, vertically centered
      legend.attr('transform', `translate(${innerWidth + 20}, ${innerHeight / 2 - (groups.length * 18) / 2})`);
    }
    
    legendItems.exit().remove();

    // Brush
    let brushG = root.select('g.brush');
    if (brushEnabled) {
      if (brushG.empty()) brushG = root.insert('g', ':first-child').attr('class', 'brush');
      const brush = d3.brushX()
        .extent([[0, 0], [innerWidth, innerHeight]])
        .on('end', (event) => {
          if (!event.selection) return;
          const [x0, x1] = event.selection;
          setBrushDomain([x.invert(x0), x.invert(x1)]);
          brushG.call(brush.move, null);
        });
      brushG.call(brush);
      svg.on('dblclick', () => setBrushDomain(null));
    } else {
      brushG.remove();
    }
  }, [data, plotSize, plotMargin, xAccessor, yAccessor, seriesAccessor, lineStyleAccessor, lineWidthAccessor, xLabel, yLabel, transitionDuration, showPoints, showLines, pointRadius, lineWidth, colors, xDomainOverride, yDomainOverride, brushEnabled, brushDomain, windowWidth]);

  const showReset = !!brushDomain;
  
  // Calculate responsive dimensions for viewBox
  const isLegendBottom = windowWidth < 950;
  const shouldTiltXAxis = windowWidth < 550;
  const extraWidthForLegend = isLegendBottom ? 0 : 100;
  const extraHeightForLegend = isLegendBottom ? 60 : 0;
  const extraHeightForTiltedLabels = shouldTiltXAxis ? 20 : 0;
  const svgWidth = plotSize + extraWidthForLegend;
  const svgHeight = Math.round(plotSize * 0.66) + extraHeightForLegend + extraHeightForTiltedLabels;

  return (
    <div ref={containerRef} className="w-full min-w-[320px] relative">
      <button
        onClick={() => setBrushDomain(null)}
        className={`absolute top-2 right-2 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded ${showReset ? '' : 'hidden'}`}
      >
        Reset Zoom
      </button>
      <svg ref={svgRef} width="100%" height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="none" style={{ overflow: 'visible' }} />
      {tooltip.visible && tooltip.d && tooltipRenderer && tooltipRenderer(tooltip)}
    </div>
  );
}
